import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Plus, Rocket, Users, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const FloatingImpactLabButton = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    const saved = localStorage.getItem('impactLabButtonPosition');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Validate saved position is within current viewport
        const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
        const height = typeof window !== 'undefined' ? window.innerHeight : 1080;
        if (parsed.x > 0 && parsed.x < width - 80 && parsed.y > 0 && parsed.y < height - 80) {
          return parsed;
        }
      } catch (e) {
        // Invalid saved position, use default
      }
    }
    // Default position: bottom-right corner with safe margins
    const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
    const height = typeof window !== 'undefined' ? window.innerHeight : 1080;
    return { x: width - 120, y: height - 120 };
  });
  const dragRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    localStorage.setItem('impactLabButtonPosition', JSON.stringify(position));
  }, [position]);

  // Adjust position on window resize to keep button visible
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => {
        const maxX = window.innerWidth - 80;
        const maxY = window.innerHeight - 80;
        return {
          x: Math.max(20, Math.min(prev.x, maxX)),
          y: Math.max(20, Math.min(prev.y, maxY))
        };
      });
    };
    
    window.addEventListener('resize', handleResize);
    // Run once on mount to ensure initial position is valid
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseDown = (e) => {
    if (isExpanded) return; // Don't drag when menu is open
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.current.x;
    const newY = e.clientY - dragStart.current.y;
    
    // Keep button within viewport bounds
    const maxX = window.innerWidth - 80;
    const maxY = window.innerHeight - 80;
    
    setPosition({
      x: Math.max(20, Math.min(newX, maxX)),
      y: Math.max(20, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleClick = () => {
    if (!isDragging) {
      setIsExpanded(!isExpanded);
    }
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const actions = [
    {
      icon: Rocket,
      label: 'Start Impact Lab',
      color: 'from-purple-500 to-pink-500',
      action: () => {
        if (!isAuthenticated) {
          navigate('/auth');
          return;
        }
        navigate('/impact-lab/create');
      }
    },
    {
      icon: Plus,
      label: 'Create Challenge',
      color: 'from-blue-500 to-cyan-500',
      action: () => {
        if (!isAuthenticated) {
          navigate('/auth');
          return;
        }
        navigate('/challenges/create');
      }
    }
  ];

  return (
    <div 
      ref={dragRef}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 50,
        cursor: isDragging ? 'grabbing' : (isExpanded ? 'pointer' : 'grab')
      }}
    >
      {/* Action Buttons (appear when expanded) */}
      {isExpanded && (
        <div className="absolute bottom-20 right-0 flex flex-col gap-3 mb-2">
          {actions.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => {
                  item.action();
                  setIsExpanded(false);
                }}
                className={`group flex items-center gap-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-all pr-5 pl-4 py-3 hover:scale-105`}
              >
                <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${item.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="font-semibold text-gray-800 whitespace-nowrap">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Main Button */}
      <button
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        className={`w-16 h-16 rounded-full shadow-lg hover:shadow-2xl transition-all flex items-center justify-center group ${
          isExpanded 
            ? 'bg-gray-700' 
            : 'bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 hover:scale-110'
        } ${isDragging ? 'scale-110 shadow-2xl' : ''}`}
        title={isDragging ? 'Dragging...' : 'Impact Lab Actions'}
      >
        {isExpanded ? (
          <X className="w-7 h-7 text-white transition-transform group-hover:rotate-90" />
        ) : (
          <Sparkles className="w-7 h-7 text-white animate-pulse" />
        )}
      </button>

      {/* Drag Hint (shows on hover when not expanded) */}
      {!isExpanded && !isDragging && (
        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Drag to move • Click for menu
        </div>
      )}
    </div>
  );
};

export default FloatingImpactLabButton;
